import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check, Calculator, TrendingDown, Sparkles, Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/context/LangContext";

function AnimatedNumber({ value, decimals = 0, className = "" }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { damping: 24, stiffness: 80 });
  const display = useTransform(spring, (v) => v.toFixed(decimals));
  useEffect(() => { mv.set(value); }, [value, mv]);
  return <motion.span className={`tabular-nums ${className}`}>{display}</motion.span>;
}

export default function ValueCounter({ partners = [], settings }) {
  const { t } = useT();
  const [mode, setMode] = useState(settings?.default_mode || "simulation");
  const [plan, setPlan] = useState("adult"); // adult | junior
  const [selected, setSelected] = useState({});

  const basePrice = plan === "adult" ? (settings?.base_price_adult ?? 69) : (settings?.base_price_junior ?? 59);

  // Build selectable offers. Display the unit offer value only (per use), no monthly cumul.
  const offers = useMemo(() => {
    return partners
      .filter((p) => p.counts_in_counter !== false)
      .map((p) => {
        const unit = Math.round(p.offer_value || 0);
        return { id: p.id, name: p.name, category: p.category, offer: p.offer, monthly: unit };
      })
      .filter((o) => o.monthly > 0)
      .sort((a, b) => b.monthly - a.monthly);
  }, [partners]);

  // Auto-select top 3 for simulation mode initially
  useEffect(() => {
    if (offers.length && Object.keys(selected).length === 0) {
      const init = {};
      offers.slice(0, 3).forEach((o) => (init[o.id] = true));
      setSelected(init);
    }
  }, [offers]);

  const totalPotential = useMemo(
    () => Math.min(offers.reduce((s, o) => s + o.monthly, 0), settings?.global_monthly_cap ?? 40),
    [offers, settings]
  );
  const selectedOffers = offers.filter((o) => selected[o.id]);
  const savings = Math.min(
    selectedOffers.reduce((s, o) => s + o.monthly, 0),
    settings?.global_monthly_cap ?? 40
  );
  const perceived = Math.max(0, basePrice - (mode === "simple" ? totalPotential : savings));

  return (
    <section
      id="counter"
      data-testid="value-counter"
      className="relative overflow-hidden bg-navy-900 text-white"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-40 pointer-events-none"
           style={{
             backgroundImage: "radial-gradient(circle at 20% 20%, hsl(75 100% 50% / 0.25), transparent 40%), radial-gradient(circle at 80% 60%, hsl(222 47% 25% / 0.6), transparent 50%)",
           }} />
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <div className="container-app section-pad relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left: copy + mode selector */}
          <div className="lg:col-span-5">
            <Badge className="bg-lime-500/15 text-lime-400 hover:bg-lime-500/15 border-lime-500/20 uppercase tracking-wider text-[10px] rounded-full px-3 py-1 mb-6">
              <Sparkles className="w-3 h-3 mr-1" /> {t("counter.badge")}
            </Badge>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t("counter.title.before")}<span className="text-lime-400">{t("counter.title.highlight")}</span>.
            </h2>
            <p className="text-white/70 mt-4 text-base sm:text-lg leading-relaxed">
              {t("counter.subtitle")}
            </p>

            <div className="mt-6 inline-flex rounded-full bg-white/5 border border-white/10 p-1">
              {[
                { k: "adult", label: t("counter.plan.adult") },
                { k: "junior", label: t("counter.plan.junior") },
              ].map((p) => (
                <button
                  key={p.k}
                  onClick={() => setPlan(p.k)}
                  data-testid={`plan-${p.k}`}
                  className={`text-xs font-semibold px-4 py-2 rounded-full transition ${
                    plan === p.k ? "bg-lime-500 text-navy-900" : "text-white/80 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {[
                { k: "simple", label: t("counter.mode.simple"), icon: Calculator },
                { k: "simulation", label: t("counter.mode.simulation"), icon: TrendingDown },
                { k: "pedagogical", label: t("counter.mode.pedagogical"), icon: Info },
              ].map(({ k, label, icon: Icon }) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  data-testid={`mode-${k}`}
                  className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full border transition ${
                    mode === k
                      ? "bg-white text-navy-900 border-white"
                      : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            <p className="text-xs text-white/40 mt-6 leading-relaxed">
              {settings?.disclaimer || t("counter.disclaimer_default")}
            </p>
          </div>

          {/* Right: counter card */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 p-4 sm:p-6 md:p-10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]">
              <div className="flex items-baseline justify-between mb-6 gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">{t("counter.monthly_sub")}</div>
                  <div className="font-heading font-bold text-xl sm:text-2xl mt-1 truncate">{plan === "adult" ? t("counter.plan.label_adult") : t("counter.plan.label_junior")}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white/50 text-xs uppercase tracking-widest font-semibold">{t("counter.displayed_price")}</div>
                  <div className="font-heading font-extrabold text-3xl text-white mt-1">{basePrice}€</div>
                </div>
              </div>

              {/* Mode: Simple */}
              <AnimatePresence mode="wait">
                {mode === "simple" && (
                  <motion.div key="simple" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
                    <div className="flex items-center justify-between py-4 border-y border-white/10">
                      <span className="text-white/80">{t("counter.total_potential")}</span>
                      <span className="font-heading font-bold text-2xl text-lime-400">
                        –<AnimatedNumber value={totalPotential} />€
                      </span>
                    </div>
                  </motion.div>
                )}

                {/* Mode: Simulation */}
                {mode === "simulation" && (
                  <motion.div key="simulation" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
                    <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">{t("counter.select_offers")}</div>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scroll">
                      {offers.slice(0, 10).map((o) => {
                        const checked = !!selected[o.id];
                        return (
                          <label
                            key={o.id}
                            data-testid={`offer-toggle-${o.id}`}
                            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition border ${
                              checked ? "bg-lime-500/10 border-lime-500/30" : "bg-white/5 border-white/10 hover:border-white/20"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.id]: !!v }))}
                              className="border-white/30 data-[state=checked]:bg-lime-500 data-[state=checked]:border-lime-500 data-[state=checked]:text-navy-900"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{o.name}</div>
                              <div className="text-xs text-white/50 truncate">{o.offer}</div>
                            </div>
                            <span className={`text-sm font-heading font-bold ${checked ? "text-lime-400" : "text-white/60"}`}>–{o.monthly}€</span>
                          </label>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Mode: Pedagogical */}
                {mode === "pedagogical" && (
                  <motion.div key="pedagogical" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
                    <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">{t("counter.pedagogical_intro")}</div>
                    <div className="space-y-1.5 text-sm">
                      {offers.slice(0, 3).map((o, i) => (
                        <motion.div
                          key={o.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.08 }}
                          className="flex items-center justify-between py-3 border-b border-white/5"
                        >
                          <span className="flex items-center gap-2 text-white/80">
                            <span className="w-6 h-6 rounded-full bg-lime-500/20 text-lime-400 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                            {o.name}
                          </span>
                          <span className="text-lime-400 font-heading font-bold">–{o.monthly}€</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Final perceived cost */}
              <div className="mt-8 rounded-2xl bg-navy-950 border border-lime-500/30 p-6 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-lime-500/20 rounded-full blur-3xl" />
                <div className="flex items-end justify-between relative">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-lime-400 font-semibold flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> {t("counter.cost_label")}
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      {mode === "simple"
                        ? t("counter.after_simple")
                        : mode === "pedagogical"
                          ? t("counter.after_pedagogical")
                          : t("counter.after_n_offers", { n: selectedOffers.length, s: selectedOffers.length > 1 ? "s" : "" })}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading font-black text-5xl md:text-6xl text-lime-400" data-testid="perceived-price">
                      <AnimatedNumber value={perceived} />
                    </span>
                    <span className="font-heading font-bold text-xl text-lime-400/80">€<span className="text-base text-white/60">{t("counter.per_month")}</span></span>
                  </div>
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-lime-500 to-lime-400"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, ((basePrice - perceived) / basePrice) * 100)}%` }}
                    transition={{ type: "spring", damping: 20 }}
                  />
                </div>
                <div className="mt-2 text-xs text-white/50">
                  {t("counter.savings")} : <span className="text-lime-400 font-semibold">{Math.round(basePrice - perceived)}€</span> / {t("counter.per_month").replace("/ ","")} ({Math.round(((basePrice - perceived) / basePrice) * 100)}%)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
